import {
  type GenerationPreset,
  toPresetInput,
  useGenerationPresets,
} from "@/lib/hooks/lessonGenerator/useGenerationPresets";
import { Button } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { HiOutlineTrash } from "react-icons/hi";
import type { BookLessonSelection } from "./BookLessonPicker";
import type { GeneratorSection } from "./types";

function PresetRow({
  preset,
  onUse,
  onDelete,
}: {
  preset: GenerationPreset;
  onUse: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="group flex items-center gap-1 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/5">
      <button
        type="button"
        onClick={onUse}
        className="min-w-0 flex-1 rounded-lg px-3 py-2 text-left"
      >
        <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {preset.name}
        </span>
        <span className="mt-0.5 block truncate text-[11px] text-gray-500 dark:text-gray-400">
          {preset.sections.length} section{preset.sections.length === 1 ? "" : "s"}
          {preset.target.seriesSlug ? ` · ${preset.target.seriesSlug}` : ""}
        </span>
      </button>
      <button
        type="button"
        onClick={onDelete}
        aria-label={`Delete ${preset.name}`}
        className="mr-1 rounded-md p-1.5 text-gray-500 opacity-0 transition-opacity hover:text-error-500 focus:opacity-100 group-hover:opacity-100"
      >
        <HiOutlineTrash className="h-4 w-4" />
      </button>
    </li>
  );
}

export function PresetBar({
  target,
  sections,
  autoApply,
  onApply,
}: {
  target: BookLessonSelection;
  sections: GeneratorSection[];
  autoApply: boolean;
  onApply: (preset: GenerationPreset) => void;
}) {
  const { presets, loading, savePreset, updatePreset, deletePreset, markPresetUsed } =
    useGenerationPresets();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState<string | null>(null);
  const [appliedId, setAppliedId] = useState<string | null>(null);
  const [skippedCount, setSkippedCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const hasAutoApplied = useRef(false);

  const applied = presets.find((one) => one.id === appliedId) ?? null;

  const use = (preset: GenerationPreset) => {
    onApply(preset);
    setAppliedId(preset.id);
    setSkippedCount(preset.skippedSectionCount);
    void markPresetUsed(preset.id);
  };

  // Applying before the saved sections land would take the preset's stale copy of them.
  useEffect(() => {
    if (hasAutoApplied.current || !autoApply || loading || presets.length === 0) return;
    const mostRecent = presets[0];
    if (!mostRecent || mostRecent.sections.length === 0) return;
    hasAutoApplied.current = true;
    use(mostRecent);
  });

  useEffect(() => {
    if (!isOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isOpen]);

  const confirmName = async () => {
    if (name === null || name.trim() === "") return;
    const trimmed = name.trim();
    setSaving(true);
    try {
      const saved = await savePreset(toPresetInput(trimmed, target, sections));
      setAppliedId(saved);
      setSkippedCount(0);
      setName(null);
      toast.success(`Preset “${trimmed}” saved`);
    } catch {
      toast.error("Could not save the preset");
    } finally {
      setSaving(false);
    }
  };

  const overwrite = async () => {
    if (!applied) return;
    setSaving(true);
    try {
      await updatePreset(applied.id, toPresetInput(applied.name, target, sections));
      toast.success(`Preset “${applied.name}” updated`);
    } catch {
      toast.error("Could not update the preset");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (preset: GenerationPreset) => {
    try {
      await deletePreset(preset.id);
      if (preset.id === appliedId) setAppliedId(null);
      toast.success(`Preset “${preset.name}” deleted`);
    } catch {
      toast.error("Could not delete the preset");
    }
  };

  return (
    <div className="flex-1 space-y-1.5">
      <div className="relative flex flex-wrap items-center justify-end gap-2" ref={listRef}>
        <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)}>
          Presets{presets.length > 0 ? ` (${presets.length})` : ""}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={sections.length === 0}
          onClick={() => setName("")}
        >
          Save as new…
        </Button>
        {applied && (
          <Button
            variant="ghost"
            size="sm"
            disabled={sections.length === 0}
            isLoading={saving}
            onClick={overwrite}
          >
            Update “{applied.name}”
          </Button>
        )}

        {isOpen && presets.length > 0 && (
          <div className="panel absolute right-0 top-full z-[70] mt-2 max-h-64 w-80 overflow-hidden overflow-y-auto rounded-xl p-1 shadow-lg">
            <ul>
              {presets.map((one) => (
                <PresetRow
                  key={one.id}
                  preset={one}
                  onUse={() => {
                    use(one);
                    setIsOpen(false);
                  }}
                  onDelete={() => void remove(one)}
                />
              ))}
            </ul>
          </div>
        )}

        {name !== null && (
          <div className="panel absolute right-0 top-full z-[70] mt-2 w-80 space-y-2 rounded-xl p-3 shadow-lg">
            <label htmlFor="preset-name" className="label block">
              Save this setup as
            </label>
            <input
              id="preset-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Superkids 3 — standard lesson"
              className="w-full rounded-lg border border-gray-200 bg-background px-3 py-2 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-700"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setName(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={confirmName}
                disabled={name.trim() === ""}
                isLoading={saving}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>

      {applied && skippedCount > 0 && (
        <p className="text-right text-xs text-error-500">
          {skippedCount} section{skippedCount === 1 ? "" : "s"} left out — their reference slide no
          longer exists.
        </p>
      )}
    </div>
  );
}
